// Pass the `name` prop; registration is handled internally.
import { Label } from '@/design-system/label';
import { cn } from '@/lib/utils';
import { useController } from 'react-hook-form';
import { RadioBtnGroupContent, RadioGroupItem, RadioGroup as Root } from 'ui/radio-group';
import { useFormField } from '.';
import { UseFormFieldProps } from './type';

interface Props extends UseFormFieldProps, React.ComponentProps<typeof Root> {
  name: string;
  options: { label: string; value: string }[];
  groupLabel: string;
  className?: string;
  labelClassName?: string;
  optionsClassName?: string;
  variant?: 'button' | 'default';
}

const RadioGroup = (props: Props) => {
  const { childProps, error } = useFormField(props);
  const { options, name, groupLabel, labelClassName, optionsClassName, variant = 'button' } = props;
  const { field } = useController({ name });

  return (
    <Root
      onValueChange={field.onChange}
      className={cn(props.className)}
      value={field.value as string}
      {...childProps}
      data-test={childProps.id}
    >
      {groupLabel ? <Label className={labelClassName}>{groupLabel}</Label> : null}
      <div className={cn('flex gap-2 flex-wrap', optionsClassName)}>
        {options.map((option) => {
          const label = option.label;
          const value = option.value;

          const isSelected = field.value === value;
          const id = `${childProps.id}-${value}`;

          if (variant === 'default') {
            return (
              <label
                key={value}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground-heading"
              >
                <RadioGroupItem id={id} value={value} isSelected={isSelected} className="cursor-pointer" />
                <span>{label}</span>
              </label>
            );
          }

          return (
            <RadioBtnGroupContent
              key={value}
              className={cn(
                'border px-4 py-2 rounded-md cursor-pointer transition-colors bg-card',
                !isSelected && 'border-border-strong text-foreground-caption',
                isSelected && 'border-error-emphasis text-destructive-text font-medium',
                error && 'border-destructive text-destructive-text',
              )}
            >
              <RadioGroupItem
                value={value}
                className={cn(
                  'after:absolute has-[[data-state=checked]]:border-destructive after:inset-0 cursor-pointer',
                  isSelected && 'border-error-emphasis text-destructive-text font-medium',
                )}
                isSelected={isSelected}
              />
              {label}
            </RadioBtnGroupContent>
          );
        })}
      </div>
    </Root>
  );
};

export default RadioGroup;
